import { FooterButtons, Option, Options, superUniqueClassName } from '@fl/cmsch-fe-library';
import { isEqual } from 'lodash/fp';
import moment from 'moment';
import React, { FC, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { none, Opt } from 'ts-opt';
import { UserProfileBreederRoleFarms } from 'api/gen/UserProfileBreederRoleFarms';
import { adminAction } from 'app/admin/model';
import { useOurTranslation } from 'app/translations';
import { formHelpers, useForm } from 'utils/forms';
import { Errors } from 'utils/validator';
import { allOptionValue, allOptionValueArray } from '../../constants/consts';
import { simpleAdminSelector } from '../../model/selector';
import { CentersSettings, FarmsSettings } from '../../types/farms-settings';
import { subjectManagementFacilityFormInitialValues, SubjectManagementFacilityFormValues, SubjectManagementFacilityFormType, subjectManagementFacilityFormName } from '../../types/subject-management-facility-form-values';
import { UserProfileAndRole } from '../../types/user-profile-and-role-ids';
import styles from '../global-management-styles.sass';
interface Props {
  /** Selected user and role pair. */
  userProfileAndRole: UserProfileAndRole;
  /** Selected subject id. */
  subjectId: number;
  /** User selector options. */
  userProfileOptions: Options<number>;
  /** Role selector options. */
  roleOptions: Options<number>;
  /** Existing binding data when editing. */
  dataForEdit: Opt<UserProfileBreederRoleFarms>;
  /** Label for the user profile field. */
  userProfileTitle: string;
  /** Whether this is the breeder or organization flow. */
  subjectFacilityFormType: SubjectManagementFacilityFormType;
  /** Optional center selection settings. */
  centersSettings?: CentersSettings;
  /** Farm selection settings. */
  farmsSettings: FarmsSettings;
  /** Whether the user already has access to all farms. */
  hasAccessToAllFarms: boolean;
  /** Whether the form is in edit mode. */
  isEdit: boolean;
  /** Validation function for the form. */
  validate(values: SubjectManagementFacilityFormValues): Errors<SubjectManagementFacilityFormValues>;
  /** Called when the form is submitted. */
  onSubmit(): void;
  /** Called when the form is cancelled. */
  onCancel(): void;
  /** Searches users for the select field. */
  onUserSearch(searchText: string): void;
  /** Searches roles for the select field. */
  onRoleSearch(searchText: string): void;
  /** Triggers synchronous validation refresh. */
  onSyncValidate(): void;
}

// eslint-disable-next-line max-lines-per-function
const SubjectManagementFacilityFormBase: FC<Props> = ({
  onSubmit,
  onCancel,
  userProfileAndRole: {
    userProfile,
    role
  },
  farmsSettings: {
    allFarmTitle,
    farmOptions,
    farmTitle,
    farmsAreEmpty,
    isRequired: farmsAreRequired
  },
  userProfileOptions,
  roleOptions,
  dataForEdit,
  subjectFacilityFormType,
  subjectId,
  onUserSearch,
  onRoleSearch,
  onSyncValidate,
  userProfileTitle,
  hasAccessToAllFarms,
  centersSettings,
  isEdit,
  validate
}) => {
  const {
    id: userProfileId,
    name: userProfileName
  } = userProfile ?? {
    id: null,
    name: ''
  };
  const {
    id: roleId,
    name: roleName
  } = role ?? {
    id: null,
    name: ''
  };
  const {
    t,
    tCommon
  } = useOurTranslation('admin');
  const dispatch = useDispatch();
  const isOrganization = useMemo(() => subjectFacilityFormType === 'ORGANIZATION', [subjectFacilityFormType]);
  const allCentersInitialInForm = useMemo(() => Boolean(centersSettings && !centersSettings.centersAreEmpty), [centersSettings]);
  const initialValues = useMemo(() => {
    if (isEdit && dataForEdit.isFull) {
      return {
        approvedFrom: dataForEdit.propOrCrash('approvedFrom'),
        approvedTo: dataForEdit.prop('approvedTo').orNull(),
        centerIds: allCentersInitialInForm ? allOptionValueArray : null,
        farmIds: hasAccessToAllFarms ? allOptionValueArray : dataForEdit.prop('farms').mapIn(x => x.id).orNull(),
        subjectId,
        roleIds: dataForEdit.prop('role').prop('id').toArray(),
        userProfileId: dataForEdit.prop('user').prop('userProfileId').orNull(),
        userProfileRoleIds: dataForEdit.prop('userProfileBreederRoleId').toArray(),
        tag: 'ValidateUpdateBreederBindingManager'
      };
    } else {
      return subjectManagementFacilityFormInitialValues(userProfileId, roleId, subjectId, !farmsAreEmpty, allCentersInitialInForm);
    }
  }, [isEdit, dataForEdit, allCentersInitialInForm, hasAccessToAllFarms, subjectId, roleId, userProfileId, farmsAreEmpty]);
  const {
    Form,
    Fields,
    submitting,
    valid,
    pristine,
    asyncValidating,
    destroy,
    change
  } = useForm({
    form: subjectManagementFacilityFormName,
    initialValues,
    destroyOnUnmount: false,
    keepDirtyOnReinitialize: true,
    enableReinitialize: true,
    forceUnregisterOnUnmount: true,
    onSubmit,
    validate
  });
  const formValues = useSelector(formHelpers.formValues(subjectManagementFacilityFormName));
  const farmIds = formValues.prop('farmIds').orUndef();
  const centerIds = formValues.prop('centerIds').orUndef();
  const loading = useSelector(simpleAdminSelector.loading);
  const allCentersOption: Option<number> | undefined = useMemo(() => centersSettings ? {
    value: allOptionValue,
    label: centersSettings.allCenterTitle
  } : undefined, [centersSettings]);
  const allFarmsOption: Option<number> | undefined = useMemo(() => ({
    value: allOptionValue,
    label: allFarmTitle
  }), [allFarmTitle]);
  useEffect(() => () => {
    destroy();
    dispatch(adminAction.getBreederDetailSuccess(none));
  }, [destroy, dispatch]);
  const approveFrom = formValues.prop('approvedFrom').orUndef();
  const approveFromMoment = useMemo(() => moment(approveFrom), [approveFrom]);
  const approvedToRef = useRef<HTMLInputElement>(null);
  const handleCenterIdsChange = useCallback((newCenterIdsOpt: Opt<Array<number>>) => {
    if (centersSettings && isOrganization) {
      const newCenterIds = newCenterIdsOpt.orElse([]);
      const allFarmsSelected = isEqual(farmIds, allOptionValueArray);
      const allOptionsSelected = newCenterIds.length === centersSettings.centerOptions.length;
      const allCentersSelected = isEqual(newCenterIds, allOptionValueArray) || allOptionsSelected;
      if (allOptionsSelected) {
        change('centerIds', allOptionValueArray);
      }
      if (!allCentersSelected && allFarmsSelected) {
        change('farmIds', null);
      } else if (allCentersSelected) {
        change('farmIds', allOptionValueArray);
      }
    }
    onSyncValidate();
  }, [onSyncValidate, farmIds, isOrganization, centersSettings, change]);
  const handleFarmIdsChange = useCallback((newFarmIdsOpt: Opt<Array<number>>) => {
    if (centersSettings && isOrganization) {
      const newFarmIds = newFarmIdsOpt.orElse([]);
      const allCentersSelected = isEqual(centerIds, allOptionValueArray);
      const allOptionsSelected = newFarmIds.length === farmOptions.length;
      const allFarmsSelected = isEqual(newFarmIds, allOptionValueArray) || allOptionsSelected;
      if (allFarmsSelected) change('farmIds', allOptionValueArray);
      if (!allFarmsSelected && allCentersSelected) {
        change('centerIds', null);
      } else if (allFarmsSelected) {
        change('centerIds', allOptionValueArray);
      }
    }
    onSyncValidate();
  }, [onSyncValidate, centerIds, isOrganization, farmOptions, centersSettings, change]);
  const handleApproveFromChange = useCallback((value: Opt<string>) => {
    onSyncValidate();
    value.onSome(date => {
      const [element] = document.getElementsByClassName(superUniqueClassName);
      element?.classList.remove('ant-picker-focused');
      setTimeout(() => approvedToRef.current?.focus());
      change('approvedFrom', date);
    });
  }, [onSyncValidate, approvedToRef, change]);
  useEffect(() => {
    if (!isEdit) {
      onUserSearch(userProfileName);
      onRoleSearch(roleName);
    }
  }, [onUserSearch, onRoleSearch, isEdit, roleName, userProfileName]);
  return <Form data-sentry-element="Form" data-sentry-component="SubjectManagementFacilityFormBase" data-sentry-source-file="index.tsx">
            <div className={styles.formContainer}>
                <div className={styles.doubleWidth}>
                    <Fields.NumberSelect name="userProfileId" label={userProfileTitle} options={userProfileOptions} disabled={submitting || isEdit || Boolean(userProfileId)} onSearch={onUserSearch} onFieldChange={onSyncValidate} fieldIsLoading={loading.includes('getUsers')} asyncSearch isRequired ignoreSearchMinLength data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </div>
                {isOrganization && centersSettings && <div className={styles.doubleWidth}>
                    <Fields.NumberMultiSelect name="centerIds" label={centersSettings.centerTitle} options={centersSettings.centerOptions} disabled={submitting || isEdit || centersSettings.centersAreEmpty} selectAllOption={allCentersOption} onFieldChange={handleCenterIdsChange} isRequired={centersSettings.isRequired} ignoreSearchMinLength tooltip={centersSettings.centersAreEmpty ? t('subjectHasNoCenters') : undefined} />
                </div>}
                <div className={styles.doubleWidth}>
                    <Fields.NumberMultiSelect name="farmIds" label={farmTitle} options={farmOptions} disabled={submitting || farmsAreEmpty} selectAllOption={allFarmsOption} onFieldChange={handleFarmIdsChange} isRequired={farmsAreRequired} ignoreSearchMinLength tooltip={farmsAreEmpty ? t('subjectHasNoFarms') : undefined} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </div>
                <div className={styles.doubleWidth}>
                    <Fields.NumberMultiSelect name="roleIds" label={t('roles')} options={roleOptions} disabled={submitting || isEdit || Boolean(roleId)} onFieldChange={onSyncValidate} onSearch={onRoleSearch} fieldIsLoading={loading.includes('getRoles')} asyncSearch isRequired ignoreSearchMinLength data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </div>
                <div>
                    <Fields.DateInput name="approvedFrom" label={t('approvedFrom')} onFieldChange={handleApproveFromChange} isRequired clearable dontIncludePast disabled={isEdit} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </div>
                <div>
                    <Fields.DateInput name="approvedTo" label={t('approvedTo')} onFieldChange={onSyncValidate} clearable dontIncludePast inputRef={approvedToRef} minDate={approveFromMoment} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                </div>
            </div>
            <FooterButtons submitting={submitting} valid={valid} pristine={pristine} onCancel={onCancel} asyncValidating={asyncValidating} submitTitle={isEdit ? tCommon('save') : tCommon('add')} data-sentry-element="FooterButtons" data-sentry-source-file="index.tsx" />
        </Form>;
};

/**
 * Breeder management form.
 *
 * @example
 * <SubjectManagementFacilityForm
 *   userProfileAndRole={userProfileAndRole}
 *   subjectId={123}
 *   userProfileOptions={userProfileOptions}
 *   roleOptions={roleOptions}
 *   dataForEdit={none}
 *   userProfileTitle="Login name"
 *   subjectFacilityFormType="BREEDER"
 *   farmsSettings={farmsSettings}
 *   hasAccessToAllFarms={false}
 *   isEdit={false}
 *   validate={validate}
 *   onSubmit={handleSubmit}
 *   onCancel={handleCancel}
 *   onUserSearch={handleUserSearch}
 *   onRoleSearch={handleRoleSearch}
 *   onSyncValidate={handleSyncValidate}
 * />
 */
export const SubjectManagementFacilityForm = memo(SubjectManagementFacilityFormBase);